/**
 * Data-driven batch dispatch config.
 *
 * Batches 1, 4–37 all follow the identical optimization pattern:
 *   applyAnswerFirstLead → deStuffFirstParagraph → applyHiringHomepageLink
 *   → insertAfterFirstH2(infoGain)
 *
 * Instead of ~34 copy-pasted `if (BATCHXX_SLUGS.has(slug)) { ... }` blocks in
 * batchContentOptimizer.ts (which pushed that file past its size ceiling),
 * this module exports one ordered array. The optimizer runs a single loop.
 *
 * SLUGS sets live in batchSlugs.ts (Batches 1–29) and batchFinalContent.ts
 * (Batches 30–31, re-exported). LEADS + INFO_GAIN live in each batch*Content
 * module. This file pulls them together into one dispatch table.
 *
 * Batch 23 (original) is the lone exception: it uses insertBeforeSecondH2 and
 * has no lead/de-stuff/homepage steps, so it stays a special case in the
 * optimizer and is NOT included here.
 */
import {
  BATCH1_SLUGS,
  BATCH4_SLUGS,
  BATCH5_SLUGS,
  BATCH6_SLUGS,
  BATCH7_SLUGS,
  BATCH8_SLUGS,
  BATCH9_SLUGS,
  BATCH10_SLUGS,
  BATCH11_SLUGS,
  BATCH12_SLUGS,
  BATCH13_SLUGS,
  BATCH14_SLUGS,
  BATCH15_SLUGS,
  BATCH16_SLUGS,
  BATCH17_SLUGS,
  BATCH18_SLUGS,
  BATCH19_SLUGS,
  BATCH20_SLUGS,
  BATCH21_SLUGS,
  BATCH22_SLUGS,
  BATCH23SEQ_SLUGS,
  BATCH24_SLUGS,
  BATCH25_SLUGS,
  BATCH26_SLUGS,
  BATCH27_SLUGS,
  BATCH28_SLUGS,
  BATCH29_SLUGS,
} from "@/lib/batchSlugs";
import { BATCH1_INFO_GAIN, BATCH1_LEADS } from "@/lib/batch1Content";
import { BATCH4_INFO_GAIN, BATCH4_LEADS } from "@/lib/batch4Content";
import { BATCH5_INFO_GAIN, BATCH5_LEADS } from "@/lib/batch5Content";
import { BATCH6_INFO_GAIN, BATCH6_LEADS } from "@/lib/batch6Content";
import { BATCH7_INFO_GAIN, BATCH7_LEADS } from "@/lib/batch7Content";
import { BATCH8_INFO_GAIN, BATCH8_LEADS } from "@/lib/batch8Content";
import { BATCH9_INFO_GAIN, BATCH9_LEADS } from "@/lib/batch9Content";
import { BATCH10_INFO_GAIN, BATCH10_LEADS } from "@/lib/batch10Content";
import { BATCH11_INFO_GAIN, BATCH11_LEADS } from "@/lib/batch11Content";
import { BATCH12_INFO_GAIN, BATCH12_LEADS } from "@/lib/batch12Content";
import { BATCH13_INFO_GAIN, BATCH13_LEADS } from "@/lib/batch13Content";
import { BATCH14_INFO_GAIN, BATCH14_LEADS } from "@/lib/batch14Content";
import { BATCH15_INFO_GAIN, BATCH15_LEADS } from "@/lib/batch15Content";
import { BATCH16_INFO_GAIN, BATCH16_LEADS } from "@/lib/batch16Content";
import { BATCH17_INFO_GAIN, BATCH17_LEADS } from "@/lib/batch17Content";
import { BATCH18_INFO_GAIN, BATCH18_LEADS } from "@/lib/batch18Content";
import { BATCH19_INFO_GAIN, BATCH19_LEADS } from "@/lib/batch19Content";
import { BATCH20_INFO_GAIN, BATCH20_LEADS } from "@/lib/batch20Content";
import { BATCH21_INFO_GAIN, BATCH21_LEADS } from "@/lib/batch21Content";
import { BATCH22_INFO_GAIN, BATCH22_LEADS } from "@/lib/batch22Content";
import {
  BATCH23SEQ_INFO_GAIN,
  BATCH23SEQ_LEADS,
} from "@/lib/batch23seqContent";
import { BATCH24_INFO_GAIN, BATCH24_LEADS } from "@/lib/batch24Content";
import { BATCH25_INFO_GAIN, BATCH25_LEADS } from "@/lib/batch25Content";
import { BATCH26_INFO_GAIN, BATCH26_LEADS } from "@/lib/batch26Content";
import { BATCH27_INFO_GAIN, BATCH27_LEADS } from "@/lib/batch27Content";
import { BATCH28_INFO_GAIN, BATCH28_LEADS } from "@/lib/batch28Content";
import { BATCH29_INFO_GAIN, BATCH29_LEADS } from "@/lib/batch29Content";
import {
  BATCH30_INFO_GAIN,
  BATCH30_LEADS,
  BATCH30_SLUGS,
  BATCH31_INFO_GAIN,
  BATCH31_LEADS,
  BATCH31_SLUGS,
} from "@/lib/batchFinalContent";
import {
  BATCH32_INFO_GAIN,
  BATCH32_LEADS,
  BATCH32_SLUGS,
} from "@/lib/batch32Content";
import {
  BATCH33_INFO_GAIN,
  BATCH33_LEADS,
  BATCH33_SLUGS,
} from "@/lib/batch33Content";
import {
  BATCH34_INFO_GAIN,
  BATCH34_LEADS,
  BATCH34_SLUGS,
} from "@/lib/batch34Content";
import {
  BATCH35_INFO_GAIN,
  BATCH35_LEADS,
  BATCH35_SLUGS,
} from "@/lib/batch35Content";
import {
  BATCH36_INFO_GAIN,
  BATCH36_LEADS,
  BATCH36_SLUGS,
} from "@/lib/batch36Content";
import {
  BATCH37_INFO_GAIN,
  BATCH37_LEADS,
  BATCH37_SLUGS,
} from "@/lib/batch37Content";
import {
  BATCH38_INFO_GAIN,
  BATCH38_LEADS,
  BATCH38_SLUGS,
} from "@/lib/batch38Content";
import {
  BATCH39_INFO_GAIN,
  BATCH39_LEADS,
  BATCH39_SLUGS,
} from "@/lib/batch39Content";
import {
  BATCH40_INFO_GAIN,
  BATCH40_LEADS,
  BATCH40_SLUGS,
} from "@/lib/batch40Content";
import {
  BATCH41_INFO_GAIN,
  BATCH41_LEADS,
  BATCH41_SLUGS,
} from "@/lib/batch41Content";
import {
  BATCH42_INFO_GAIN,
  BATCH42_LEADS,
  BATCH42_SLUGS,
} from "@/lib/batch42Content";
import {
  BATCH43_INFO_GAIN,
  BATCH43_LEADS,
  BATCH43_SLUGS,
} from "@/lib/batch43Content";
import {
  BATCH44_INFO_GAIN,
  BATCH44_LEADS,
  BATCH44_SLUGS,
} from "@/lib/batch44Content";
import {
  BATCH45_INFO_GAIN,
  BATCH45_LEADS,
  BATCH45_SLUGS,
} from "@/lib/batch45Content";
import {
  BATCH46_INFO_GAIN,
  BATCH46_LEADS,
  BATCH46_SLUGS,
} from "@/lib/batch46Content";
import {
  BATCH47_INFO_GAIN,
  BATCH47_LEADS,
  BATCH47_SLUGS,
} from "@/lib/batch47Content";
import {
  BATCH48_INFO_GAIN,
  BATCH48_LEADS,
  BATCH48_SLUGS,
} from "@/lib/batch48Content";
import {
  BATCH49_INFO_GAIN,
  BATCH49_LEADS,
  BATCH49_SLUGS,
} from "@/lib/batch49Content";
import {
  BATCH50_INFO_GAIN,
  BATCH50_LEADS,
  BATCH50_SLUGS,
} from "@/lib/batch50Content";
import {
  BATCH51_INFO_GAIN,
  BATCH51_LEADS,
  BATCH51_SLUGS,
} from "@/lib/batch51Content";
import {
  BATCH52_INFO_GAIN,
  BATCH52_LEADS,
  BATCH52_SLUGS,
} from "@/lib/batch52Content";
import {
  BATCH53_INFO_GAIN,
  BATCH53_LEADS,
  BATCH53_SLUGS,
} from "@/lib/batch53Content";
import {
  BATCH54_INFO_GAIN,
  BATCH54_LEADS,
  BATCH54_SLUGS,
} from "@/lib/batch54Content";
import {
  BATCH55_INFO_GAIN,
  BATCH55_LEADS,
  BATCH55_SLUGS,
} from "@/lib/batch55Content";
import {
  BATCH56_INFO_GAIN,
  BATCH56_LEADS,
  BATCH56_SLUGS,
} from "@/lib/batch56Content";
import {
  BATCH57_INFO_GAIN,
  BATCH57_LEADS,
  BATCH57_SLUGS,
} from "@/lib/batch57Content";
import {
  BATCH58_INFO_GAIN,
  BATCH58_LEADS,
  BATCH58_SLUGS,
} from "@/lib/batch58Content";
import {
  BATCH59_INFO_GAIN,
  BATCH59_LEADS,
  BATCH59_SLUGS,
} from "@/lib/batch59Content";
import {
  BATCH60_INFO_GAIN,
  BATCH60_LEADS,
  BATCH60_SLUGS,
} from "@/lib/batch60Content";
import {
  BATCH61_INFO_GAIN,
  BATCH61_LEADS,
  BATCH61_SLUGS,
} from "@/lib/batch61Content";
import {
  BATCH62_INFO_GAIN,
  BATCH62_LEADS,
  BATCH62_SLUGS,
} from "@/lib/batch62Content";
import {
  BATCH63_INFO_GAIN,
  BATCH63_LEADS,
  BATCH63_SLUGS,
} from "@/lib/batch63Content";
import {
  BATCH64_INFO_GAIN,
  BATCH64_LEADS,
  BATCH64_SLUGS,
} from "@/lib/batch64Content";
import {
  BATCH65_INFO_GAIN,
  BATCH65_LEADS,
  BATCH65_SLUGS,
} from "@/lib/batch65Content";
import {
  BATCH66_INFO_GAIN,
  BATCH66_LEADS,
  BATCH66_SLUGS,
} from "@/lib/batch66Content";
import {
  BATCH67_INFO_GAIN,
  BATCH67_LEADS,
  BATCH67_SLUGS,
} from "@/lib/batch67Content";
import {
  BATCH68_INFO_GAIN,
  BATCH68_LEADS,
  BATCH68_SLUGS,
} from "@/lib/batch68Content";
import {
  BATCH69_INFO_GAIN,
  BATCH69_LEADS,
  BATCH69_SLUGS,
} from "@/lib/batch69Content";
import {
  BATCH70_INFO_GAIN,
  BATCH70_LEADS,
  BATCH70_SLUGS,
} from "@/lib/batch70Content";
import {
  BATCH71_INFO_GAIN,
  BATCH71_LEADS,
  BATCH71_SLUGS,
} from "@/lib/batch71Content";
import {
  BATCH72_INFO_GAIN,
  BATCH72_LEADS,
  BATCH72_SLUGS,
} from "@/lib/batch72Content";
import {
  BATCH73_INFO_GAIN,
  BATCH73_LEADS,
  BATCH73_SLUGS,
} from "@/lib/batch73Content";
import {
  BATCH74_INFO_GAIN,
  BATCH74_LEADS,
  BATCH74_SLUGS,
} from "@/lib/batch74Content";
import {
  BATCH75_INFO_GAIN,
  BATCH75_LEADS,
  BATCH75_SLUGS,
} from "@/lib/batch75Content";

export interface BatchDispatchEntry {
  slugs: Set<string>;
  leads: Record<string, string>;
  infoGain: Record<string, string>;
}

/** Ordered batch dispatch table (Batches 1, 4–37, sequential 23). */
export const BATCH_DISPATCH: BatchDispatchEntry[] = [
  { slugs: BATCH1_SLUGS, leads: BATCH1_LEADS, infoGain: BATCH1_INFO_GAIN },
  { slugs: BATCH4_SLUGS, leads: BATCH4_LEADS, infoGain: BATCH4_INFO_GAIN },
  { slugs: BATCH5_SLUGS, leads: BATCH5_LEADS, infoGain: BATCH5_INFO_GAIN },
  { slugs: BATCH6_SLUGS, leads: BATCH6_LEADS, infoGain: BATCH6_INFO_GAIN },
  { slugs: BATCH7_SLUGS, leads: BATCH7_LEADS, infoGain: BATCH7_INFO_GAIN },
  { slugs: BATCH8_SLUGS, leads: BATCH8_LEADS, infoGain: BATCH8_INFO_GAIN },
  { slugs: BATCH9_SLUGS, leads: BATCH9_LEADS, infoGain: BATCH9_INFO_GAIN },
  { slugs: BATCH10_SLUGS, leads: BATCH10_LEADS, infoGain: BATCH10_INFO_GAIN },
  { slugs: BATCH11_SLUGS, leads: BATCH11_LEADS, infoGain: BATCH11_INFO_GAIN },
  { slugs: BATCH12_SLUGS, leads: BATCH12_LEADS, infoGain: BATCH12_INFO_GAIN },
  { slugs: BATCH13_SLUGS, leads: BATCH13_LEADS, infoGain: BATCH13_INFO_GAIN },
  { slugs: BATCH14_SLUGS, leads: BATCH14_LEADS, infoGain: BATCH14_INFO_GAIN },
  { slugs: BATCH15_SLUGS, leads: BATCH15_LEADS, infoGain: BATCH15_INFO_GAIN },
  { slugs: BATCH16_SLUGS, leads: BATCH16_LEADS, infoGain: BATCH16_INFO_GAIN },
  { slugs: BATCH17_SLUGS, leads: BATCH17_LEADS, infoGain: BATCH17_INFO_GAIN },
  { slugs: BATCH18_SLUGS, leads: BATCH18_LEADS, infoGain: BATCH18_INFO_GAIN },
  { slugs: BATCH19_SLUGS, leads: BATCH19_LEADS, infoGain: BATCH19_INFO_GAIN },
  { slugs: BATCH20_SLUGS, leads: BATCH20_LEADS, infoGain: BATCH20_INFO_GAIN },
  { slugs: BATCH21_SLUGS, leads: BATCH21_LEADS, infoGain: BATCH21_INFO_GAIN },
  { slugs: BATCH22_SLUGS, leads: BATCH22_LEADS, infoGain: BATCH22_INFO_GAIN },
  {
    slugs: BATCH23SEQ_SLUGS,
    leads: BATCH23SEQ_LEADS,
    infoGain: BATCH23SEQ_INFO_GAIN,
  },
  { slugs: BATCH24_SLUGS, leads: BATCH24_LEADS, infoGain: BATCH24_INFO_GAIN },
  { slugs: BATCH25_SLUGS, leads: BATCH25_LEADS, infoGain: BATCH25_INFO_GAIN },
  { slugs: BATCH26_SLUGS, leads: BATCH26_LEADS, infoGain: BATCH26_INFO_GAIN },
  { slugs: BATCH27_SLUGS, leads: BATCH27_LEADS, infoGain: BATCH27_INFO_GAIN },
  { slugs: BATCH28_SLUGS, leads: BATCH28_LEADS, infoGain: BATCH28_INFO_GAIN },
  { slugs: BATCH29_SLUGS, leads: BATCH29_LEADS, infoGain: BATCH29_INFO_GAIN },
  { slugs: BATCH30_SLUGS, leads: BATCH30_LEADS, infoGain: BATCH30_INFO_GAIN },
  { slugs: BATCH31_SLUGS, leads: BATCH31_LEADS, infoGain: BATCH31_INFO_GAIN },
  { slugs: BATCH32_SLUGS, leads: BATCH32_LEADS, infoGain: BATCH32_INFO_GAIN },
  { slugs: BATCH33_SLUGS, leads: BATCH33_LEADS, infoGain: BATCH33_INFO_GAIN },
  { slugs: BATCH34_SLUGS, leads: BATCH34_LEADS, infoGain: BATCH34_INFO_GAIN },
  { slugs: BATCH35_SLUGS, leads: BATCH35_LEADS, infoGain: BATCH35_INFO_GAIN },
  { slugs: BATCH36_SLUGS, leads: BATCH36_LEADS, infoGain: BATCH36_INFO_GAIN },
  { slugs: BATCH37_SLUGS, leads: BATCH37_LEADS, infoGain: BATCH37_INFO_GAIN },
  { slugs: BATCH38_SLUGS, leads: BATCH38_LEADS, infoGain: BATCH38_INFO_GAIN },
  { slugs: BATCH39_SLUGS, leads: BATCH39_LEADS, infoGain: BATCH39_INFO_GAIN },
  { slugs: BATCH40_SLUGS, leads: BATCH40_LEADS, infoGain: BATCH40_INFO_GAIN },
  { slugs: BATCH41_SLUGS, leads: BATCH41_LEADS, infoGain: BATCH41_INFO_GAIN },
  { slugs: BATCH42_SLUGS, leads: BATCH42_LEADS, infoGain: BATCH42_INFO_GAIN },
  { slugs: BATCH43_SLUGS, leads: BATCH43_LEADS, infoGain: BATCH43_INFO_GAIN },
  { slugs: BATCH44_SLUGS, leads: BATCH44_LEADS, infoGain: BATCH44_INFO_GAIN },
  { slugs: BATCH45_SLUGS, leads: BATCH45_LEADS, infoGain: BATCH45_INFO_GAIN },
  { slugs: BATCH46_SLUGS, leads: BATCH46_LEADS, infoGain: BATCH46_INFO_GAIN },
  { slugs: BATCH47_SLUGS, leads: BATCH47_LEADS, infoGain: BATCH47_INFO_GAIN },
  { slugs: BATCH48_SLUGS, leads: BATCH48_LEADS, infoGain: BATCH48_INFO_GAIN },
  { slugs: BATCH49_SLUGS, leads: BATCH49_LEADS, infoGain: BATCH49_INFO_GAIN },
  { slugs: BATCH50_SLUGS, leads: BATCH50_LEADS, infoGain: BATCH50_INFO_GAIN },
  { slugs: BATCH51_SLUGS, leads: BATCH51_LEADS, infoGain: BATCH51_INFO_GAIN },
  { slugs: BATCH52_SLUGS, leads: BATCH52_LEADS, infoGain: BATCH52_INFO_GAIN },
  { slugs: BATCH53_SLUGS, leads: BATCH53_LEADS, infoGain: BATCH53_INFO_GAIN },
  { slugs: BATCH54_SLUGS, leads: BATCH54_LEADS, infoGain: BATCH54_INFO_GAIN },
  { slugs: BATCH55_SLUGS, leads: BATCH55_LEADS, infoGain: BATCH55_INFO_GAIN },
  { slugs: BATCH56_SLUGS, leads: BATCH56_LEADS, infoGain: BATCH56_INFO_GAIN },
  { slugs: BATCH57_SLUGS, leads: BATCH57_LEADS, infoGain: BATCH57_INFO_GAIN },
  { slugs: BATCH58_SLUGS, leads: BATCH58_LEADS, infoGain: BATCH58_INFO_GAIN },
  { slugs: BATCH59_SLUGS, leads: BATCH59_LEADS, infoGain: BATCH59_INFO_GAIN },
  { slugs: BATCH60_SLUGS, leads: BATCH60_LEADS, infoGain: BATCH60_INFO_GAIN },
  { slugs: BATCH61_SLUGS, leads: BATCH61_LEADS, infoGain: BATCH61_INFO_GAIN },
  { slugs: BATCH62_SLUGS, leads: BATCH62_LEADS, infoGain: BATCH62_INFO_GAIN },
  { slugs: BATCH63_SLUGS, leads: BATCH63_LEADS, infoGain: BATCH63_INFO_GAIN },
  { slugs: BATCH64_SLUGS, leads: BATCH64_LEADS, infoGain: BATCH64_INFO_GAIN },
  { slugs: BATCH65_SLUGS, leads: BATCH65_LEADS, infoGain: BATCH65_INFO_GAIN },
  { slugs: BATCH66_SLUGS, leads: BATCH66_LEADS, infoGain: BATCH66_INFO_GAIN },
  { slugs: BATCH67_SLUGS, leads: BATCH67_LEADS, infoGain: BATCH67_INFO_GAIN },
  { slugs: BATCH68_SLUGS, leads: BATCH68_LEADS, infoGain: BATCH68_INFO_GAIN },
  { slugs: BATCH69_SLUGS, leads: BATCH69_LEADS, infoGain: BATCH69_INFO_GAIN },
  { slugs: BATCH70_SLUGS, leads: BATCH70_LEADS, infoGain: BATCH70_INFO_GAIN },
  { slugs: BATCH71_SLUGS, leads: BATCH71_LEADS, infoGain: BATCH71_INFO_GAIN },
  { slugs: BATCH72_SLUGS, leads: BATCH72_LEADS, infoGain: BATCH72_INFO_GAIN },
  { slugs: BATCH73_SLUGS, leads: BATCH73_LEADS, infoGain: BATCH73_INFO_GAIN },
  { slugs: BATCH74_SLUGS, leads: BATCH74_LEADS, infoGain: BATCH74_INFO_GAIN },
  { slugs: BATCH75_SLUGS, leads: BATCH75_LEADS, infoGain: BATCH75_INFO_GAIN },
];
